import React, {useState, useEffect, useMemo, useRef} from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, LineController, BarElement, Title, Tooltip, Legend, TimeScale, ChartOptions, ChartData, Plugin } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { getTimeSeriesData } from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, BarElement, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}


interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        total_count: number;
    };
}

interface LightningData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        start_date: string;
        end_date: string;
        count: number;
        total_count: number;
    };
}

interface DailyDataPoint {
    date: string;
    precipitation: number;
    lightning: number;
}

interface LightningPrecipitationChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
    showLightningNoPrecipitation?: boolean;
}

const LightningPrecipitationChart: React.FC<LightningPrecipitationChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400 ,
    showLightningNoPrecipitation = false
}) => {
    // State management
    const [precipitationData, setPrecipitationData] = useState<TimeSeriesData | null>(null);
    const [lightningData, setLightningData] = useState<LightningData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [chartKey, setChartKey] = useState<number>(0); // Add key to force chart re-render


    const chartRef = useRef<any>(null);

    // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

    // Data aggregation function
    const aggregateToDaily = (precipData: TimeSeriesDataPoint[], lightData: TimeSeriesDataPoint[]): DailyDataPoint[] => {
        const rows: DailyDataPoint[] = [];

  // Map precipitation points (keep original bins as-is)
  for (const p of precipData || []) {
    if (p.value !== null) {
      rows.push({
        date: moment(p.timestamp).format('YYYY-MM-DD'),
        precipitation: p.value,
        lightning: 0
      });
    }
  }

  // Map lightning points (keep original bins as-is)
  for (const l of lightData || []) {
    if (l.value !== null) {
      rows.push({
        date: moment(l.timestamp).format('YYYY-MM-DD'),
        precipitation: 0,
        lightning: l.value
      });
    }
  }

  // Just sort; no merging of same-date rows
  rows.sort((a, b) => a.date.localeCompare(b.date));
  return rows;

    };

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch both datasets in parallel
                const [precipitationResponse, lightningResponse] = await Promise.allSettled([
                    getTimeSeriesData(stationId, 'pr', startDate, endDate),
                    getTimeSeriesData(stationId, 'le', startDate, endDate)
                ]);

                // Handle precipitation data response
                if (precipitationResponse.status === 'fulfilled') {
                    setPrecipitationData(precipitationResponse.value);
                } else {
                    console.error('Failed to fetch precipitation data:', precipitationResponse.reason);
                }

                // Handle lightning data response
                if (lightningResponse.status === 'fulfilled') {
                    setLightningData(lightningResponse.value);
                } else {
                    console.error('Failed to fetch lightning data:', lightningResponse.reason);
                }

            } catch (err) {
                setError('Failed to fetch comparison data');
                console.error('Error fetching comparison data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, startDate, endDate]);

        // Force chart re-render when showLightningNoPrecipitation changes
    useEffect(() => {
        setChartKey(prev => prev + 1);
    }, [showLightningNoPrecipitation]);


    // Process data to daily aggregates
    const dailyData = useMemo(() => {
        if (!precipitationData?.data && !lightningData?.data) return [];

        return aggregateToDaily(
            precipitationData?.data || [],
            lightningData?.data || []
        );
    }, [precipitationData, lightningData]);

    // Create annotation plugin for lightning without rain
    const lightningNoRainPlugin: Plugin<'bar' | 'line'> = useMemo(() => ({
        id: 'lightningNoRainAnnotation',
        beforeDraw: (chart) => {
            if (!showLightningNoPrecipitation || !dailyData.length) return;

            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const xScale = chart.scales.x;

            // Find days with lightning but no/minimal rain (< 0.5mm)
            const lightningNoRainDays = dailyData.filter(point =>
                point.lightning > 0 && point.precipitation < 0.5
            );

            if (!lightningNoRainDays.length) return;

            lightningNoRainDays.forEach(day => {
                const dayTimestamp = moment(day.date).valueOf();
                const x = xScale.getPixelForValue(dayTimestamp);

                // Calculate bar width for proper positioning
                const nextDayTimestamp = moment(day.date).add(1, 'day').valueOf();
                const nextX = xScale.getPixelForValue(nextDayTimestamp);
                const barWidth = Math.abs(nextX - x);

                // Draw background rectangle
                ctx.save();
                ctx.fillStyle = 'rgba(255, 165, 0, 0.2)'; // Orange with transparency
                ctx.fillRect(x - barWidth/2, chartArea.top, barWidth, chartArea.bottom - chartArea.top);

                // Draw border
                ctx.strokeStyle = '#ff8c00';
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);
                ctx.strokeRect(x - barWidth/2, chartArea.top, barWidth, chartArea.bottom - chartArea.top);

                // Draw label at the top
                ctx.fillStyle = '#ff8c00';
                ctx.font = 'bold 10px Arial';
                ctx.textAlign = 'center';

                // Draw background for text
                const labelText = 'Lightning\nNo Rain';
                const lines = labelText.split('\n');
                const lineHeight = 12;
                const totalHeight = lines.length * lineHeight + 4;
                const maxWidth = Math.max(...lines.map(line => ctx.measureText(line).width));

                ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                ctx.fillRect(x - maxWidth/2 - 3, chartArea.top + 5, maxWidth + 6, totalHeight);

                // Draw border around text background
                ctx.strokeStyle = '#ff8c00';
                ctx.lineWidth = 1;
                ctx.setLineDash([]);
                ctx.strokeRect(x - maxWidth/2 - 3, chartArea.top + 5, maxWidth + 6, totalHeight);

                // Draw text
                ctx.fillStyle = '#ff8c00';
                lines.forEach((line, index) => {
                    ctx.fillText(line, x, chartArea.top + 16 + (index * lineHeight));
                });

                ctx.restore();
            });
        }
    }), [showLightningNoPrecipitation, dailyData]);


    // Chart data configuration - using time-based data like precipitation comparison
    const chartData: ChartData<'bar' | 'line'> = useMemo(() => {
        if (!dailyData.length) return { datasets: [] };

        const precipitationData = dailyData.map(point => ({
            x: moment(point.date).valueOf(),
            y: point.precipitation
        }));

        const lightningData = dailyData.map(point => ({
            x: moment(point.date).valueOf(),
            y: point.lightning
        }));

        return {
            datasets: [
                {
                    label: 'Precipitation (mm)',
                    data: precipitationData,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                    yAxisID: 'y-precipitation',
                    type: 'bar'
                },
                {
                    label: 'Lightning Strikes',
                    data: lightningData,
                    backgroundColor: 'rgba(255, 206, 86, 0.6)',
                    borderColor: 'rgba(255, 206, 86, 1)',
                    borderWidth: 2,
                    yAxisID: 'y-lightning',
                    type: 'line',
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    fill: false,
                    tension: 0.1
                }
            ]
        };
    }, [dailyData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'bar' | 'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'index',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: 'Daily Lightning Activity vs Precipitation',
                font: {
                    size: 16
                }
            },
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        if (context.datasetIndex === 0) {
                            return `Precipitation: ${value?.toFixed(3) || 0} mm`;
                        } else {
                            return `Lightning Strikes: ${value || 0}`;
                        }
                    },
                    afterBody: (tooltipItems) => {
                        // Add extra info for lightning without rain days
                        const xValue = tooltipItems[0]?.parsed?.x;
                        if (xValue) {
                            const date = moment(xValue).format('YYYY-MM-DD');
                            const dayData = dailyData.find(d => d.date === date);
                            if (dayData && dayData.lightning > 0 && dayData.precipitation < 0.5) {
                                return ['', '⚠️ Lightning without significant rain'];
                            }
                        }
                        return [];
                    }

                }
            },
             // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM'
                    },
                    tooltipFormat: 'DD MMM'
                },
                title: {
                    display: true,
                    text: 'Date'
                }
            },
            'y-precipitation': {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Precipitation (mm)'
                },
                beginAtZero: true,
                ticks: {
                    callback: function(value: any) {
                        return `${value.toFixed(1)} mm`;
                    }
                }
            },
            'y-lightning': {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Lightning Strikes'
                },
                beginAtZero: true,
                grid: {
                    drawOnChartArea: false
                },
                ticks: {
                    callback: function(value: any) {
                        return `${Math.floor(value)}`;
                    }
                }
            }
        },
         parsing: false,
        normalized: true,
        spanGaps: false
    }), [dailyData]);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center'  }}>
                        <div className="text-muted">
                            <i className="fa fa-chart-bar fa-3x mb-3"></i> 
                            {/* //fa fa-spinner fa-spin fa-3x mb-3 */}
                            <p>Loading lightning and precipitation data...</p>
                        </div>
                    </div>
                </div>
            </div>

        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    // No data state
    if (!dailyData.length) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-warning">No lightning or precipitation data available for comparison</div>
                </div>
            </div>
        );
    }


    // Calculate summary statistics
    const lightningNoRainDays = dailyData.filter(point =>
        point.lightning > 0 && point.precipitation < 0.5
    ).length;


    // Main render
    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    {/*<h5 className="card-title mb-0">{title}</h5>*/}
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>

                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Chart  key={chartKey} ref={chartRef} type="bar" data={chartData} options={chartOptions} plugins={showLightningNoPrecipitation ? [lightningNoRainPlugin] : []}/>
                </div>
                <div className="mt-2">
                    <small className="text-muted">
                        Lightning strikes vs precipitation comparison (daily totals)
                        <span className="ms-3">
                            Days with data: {dailyData.length}
                        </span>
                        <span className="ms-3">
                            Total lightning: {dailyData.reduce((sum, d) => sum + d.lightning, 0)}
                        </span>
                        <span className="ms-3">
                            Total precipitation: {dailyData.reduce((sum, d) => sum + d.precipitation, 0).toFixed(1)} mm
                        </span>
                        <span className="ms-3 text-warning">
                            <i className="fa fa-bolt"></i> Lightning without rain: {lightningNoRainDays} days
                        </span>
                    </small>
                </div>
            </div>
        </div>
    );
};

export default LightningPrecipitationChart;